import { Updater } from "use-immer";
import { Dispatch, SetStateAction } from "react";

import { SortSettings } from "../../component/sortable-table/SortableTable";
import { SortOrder } from "./constants";
import { PagePermissionMask, WasteTotalVerticalEntry } from "./interfaces";

// Date range manipulation
export const thisWeekRangeSelect = (): [Date | null, Date | null] => {
  const today = new Date();
  const day = today.getDay();
  const startDate = new Date(today.setDate(today.getDate() - day));
  return [startDate, new Date()];
};

export const previousWeekRangeSelect = (): [Date | null, Date | null] => {
  const today = new Date();
  const day = today.getDay();
  const startDate = new Date(today.setDate(today.getDate() - day - 7));
  const endDate = new Date(today.setDate(today.getDate() + 6));
  return [startDate, endDate];
};

export const currentMonthRangeSelect = (): [Date | null, Date | null] => {
  const today = new Date();
  const dd = today.getDate();
  const mm = today.getMonth() + 1;
  const yyyy = today.getFullYear();
  const startDate = new Date(mm + "/01/" + yyyy);
  const endDate = new Date(mm + "/" + dd + "/" + yyyy);
  return [startDate, endDate];
};

export const previousMonthRangeSelect = (): [Date | null, Date | null] => {
  const today = new Date();
  const mm = today.getMonth() > 0 ? today.getMonth() : 12;
  const yyyy = mm === 12 ? today.getFullYear() - 1 : today.getFullYear();
  const dd =
    mm === 4 || mm === 6 || mm === 9 || mm === 11
      ? 30
      : mm === 2
      ? yyyy % 4 === 0
        ? 29
        : 28
      : 31;
  const startDate = new Date(mm + "/01/" + yyyy);
  const endDate = new Date(mm + "/" + dd + "/" + yyyy);
  return [startDate, endDate];
};

export const currentYearRangeSelect = (): [Date | null, Date | null] => {
  const today = new Date();
  const dd = today.getDate();
  const mm = today.getMonth() + 1;
  const yyyy = today.getFullYear();
  const startDate = new Date("01/01/" + yyyy);
  const endDate = new Date(mm + "/" + dd + "/" + yyyy);
  return [startDate, endDate];
};

export const previousYearRangeSelect = (): [Date | null, Date | null] => {
  const today = new Date();
  const yyyy = today.getFullYear() - 1;
  const startDate = new Date("01/01/" + yyyy);
  const endDate = new Date("12/31/" + yyyy);
  return [startDate, endDate];
};

export const currentQuarterRangeSelect = (): [Date | null, Date | null] => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = today.getMonth();
  const quarterNo = Math.ceil((mm + 1) / 3);
  const startDateStr = `${yyyy}-${(quarterNo * 3 - 2)
    .toString()
    .padStart(2, "0")}-01T00:00:00+0000`;
  const startDate = new Date(Date.parse(startDateStr));
  return [startDate, today];
};

export const previousQuarterRangeSelect = (): [Date | null, Date | null] => {
  const lastDayOfMonths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const today = new Date();
  const mm = today.getMonth();
  const quarterNo =
    Math.ceil((mm + 1) / 3) - 1 > 0 ? Math.ceil((mm + 1) / 3) - 1 : 4;
  const yyyy = quarterNo === 4 ? today.getFullYear() - 1 : today.getFullYear();
  const startDateStr = `${yyyy}-${(quarterNo * 3 - 2)
    .toString()
    .padStart(2, "0")}-01T00:00:00+0000`;
  const endDateStr = `${yyyy}-${(quarterNo * 3).toString().padStart(2, "0")}-${
    lastDayOfMonths[quarterNo * 3 - 1]
  }T00:00:00+0000`;
  const startDate = new Date(Date.parse(startDateStr));
  const endDate = new Date(Date.parse(endDateStr));
  return [startDate, endDate];
};

export const lastYearSpanRangeSelect = (): [Date | null, Date | null] => {
  const today = new Date();
  const mm = today.getMonth() + 1;
  const yyyy = today.getFullYear() - 1;
  const dd = today.getDate() === 29 && mm === 2 ? 28 : today.getDate();
  const startDate = new Date(mm + "/" + dd + "/" + yyyy);
  return [startDate, today];
};

// Miscellaneous
export const validateEmail = (email: string) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const validatePassword = (pwd: string) => {
  if (pwd.length < 8 || pwd.length > 20) return false;
  const checkNum = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"].findIndex(
    (ch) => pwd.includes(ch)
  );
  if (checkNum < 0) return false;
  const checkSymbol = ["@", "$", "!", "%", "*", "#", "?", "&"].findIndex((ch) =>
    pwd.includes(ch)
  );
  if (checkSymbol < 0) return false;
  return true;
};

const uuidRegex =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const validateUUID = (uuid: string) => {
  return uuidRegex.test(uuid);
};

export const inputDateFormat = (date: Date) => {
  const mm = date.getMonth() + 1;
  const yyyy = date.getFullYear();
  const dd = date.getDate();
  return (
    yyyy + "" + (mm >= 10 ? mm : "0" + mm) + "" + (dd >= 10 ? dd : "0" + dd)
  );
};

export const displayDateFormat = (date: Date) => {
  const mm = date.getMonth() + 1;
  const yyyy = date.getFullYear();
  const dd = date.getDate();
  return (
    yyyy + "-" + (mm >= 10 ? mm : "0" + mm) + "-" + (dd >= 10 ? dd : "0" + dd)
  );
};

export const decimalPlaces = (n: number) => {
  var s = "" + +n;
  var match = /(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/.exec(s);
  if (!match) return 0;
  return Math.max(
    0,
    (match[1] === "0" ? 0 : (match[1] || "").length) - (match[2] || "").length
  );
};

export const sortFunction = (
  a: WasteTotalVerticalEntry,
  b: WasteTotalVerticalEntry,
  valType: number,
  sort: SortOrder
) => {
  if (sort === SortOrder.Descending) {
    switch (valType) {
      case 1:
        return b.value1 - a.value1;
      case 2:
        return b.value2 - a.value2;
      default:
        return b.value3 - a.value3;
    }
  } else {
    switch (valType) {
      case 1:
        return a.value1 - b.value1;
      case 2:
        return a.value2 - b.value2;
      default:
        return a.value3 - b.value3;
    }
  }
};

export const sortTableFunction = (
  a: any[],
  b: any[],
  sortSettings: SortSettings
) => {
  const aValue = a[sortSettings.column];
  const bValue = b[sortSettings.column];
  if (typeof aValue === "number" && typeof bValue === "number") {
    return sortSettings.order === SortOrder.Descending
      ? bValue - aValue
      : aValue - bValue;
  }
  return sortSettings.order === SortOrder.Descending
    ? bValue.localeCompare(aValue)
    : aValue.localeCompare(bValue);
};

export const leaderboardReverseSorting = (
  filterLeaderboardValue: number,
  sortingLeaderboardValue: SortOrder,
  recycledWasteBySite: WasteTotalVerticalEntry[],
  setRecycledWasteBySite: Updater<WasteTotalVerticalEntry[]>,
  setSortingLeaderboardValue: Dispatch<SetStateAction<SortOrder>>
) => {
};

export const changeFilterLeaderboardAction = (
  filterVal: number,
  recycledWasteBySite: WasteTotalVerticalEntry[],
  setFilterLeaderboardValue: Dispatch<SetStateAction<number>>,
  setRecycledWasteBySite: Updater<WasteTotalVerticalEntry[]>,
  setSortingLeaderboardValue: Dispatch<SetStateAction<SortOrder>>
) => {
};

export const checkPagePermissions = (
  pageName: string,
  tippingUser: boolean,
  permsMask?: PagePermissionMask
) => {
  if (permsMask) {
    if (pageName === "Invoices" && (tippingUser || !permsMask.financialPage))
      return false;
    if (
      (pageName === "Weighting Platform" ||
        pageName === "API Connections Dashboard" ||
        pageName === "Add New API Connection") &&
      (tippingUser || !permsMask.weightingPage)
    )
      return false;
    if (
      pageName === "Sustainability Audits" &&
      (tippingUser || !permsMask.sustainabilityAudit)
    )
      return false;
    if (pageName === "Reporting" && !tippingUser && !permsMask.reportingPage)
      return false;
    if (pageName === "Documents" && tippingUser) return false;
  }
  return true;
};

export const redirectToPage = (url: any) => {
  if (url && url.length > 0) {
    const win = window.open(url, "_blank");
    if (win) win.focus();
  }
};

export const base64ToBlob = (base64String: string, contentType = "") => {
  const byteCharacters = window.atob(base64String);
  const byteArrays = [];
  for (let i = 0; i < byteCharacters.length; i++) {
    byteArrays.push(byteCharacters.charCodeAt(i));
  }
  const byteArray = new Uint8Array(byteArrays);
  return new Blob([byteArray], { type: contentType });
};

export const retrieveFileType = (ext: string) => {
  if (ext === "pdf") return "application/pdf";
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "png") return "image/png";
  if (ext === "doc" || ext === "docx") return "application/msword";
  if (ext === "xls" || ext === "xlsx") return "application/vnd.ms-excel";
  if (ext === "ppt" || ext === "pptx") return "application/vnd.ms-powerpoint";
  return "";
};

export const splitName = (name: string): string[] => {
  if (!name.includes(".")) return [];
  const nameSplit = name.split(".");
  const extension = nameSplit.pop() ?? "";
  const fileName = nameSplit.join(".");
  return [fileName, extension];
};

// Endpoints
export const confirmAccount = async (confirmationCode: string) => {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_LOCATION}/api/auth/email-confirmation?confirmation=${confirmationCode}`
    );
    const data = await res.json();
    return data;
  } catch (err) {
    console.error(err);
  }
};

export const azureAuthenticate = async (accessToken: string) => {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_LOCATION}/api/auth/microsoft/callback?access_token=${accessToken}`
    );
    const data = await res.json();
    return data;
  } catch (err) {
    console.error(err);
  }
};
